import * as array from "../utils/array";
import {Component} from "./Component";

function appendChildren(el: Node, children: Appendable[]) {
	for (const k of children) {
		if (k instanceof Node || k instanceof Element ||
				k instanceof DocumentFragment)
			el.appendChild(k);
		else if (k instanceof Array)
			appendChildren(el, k);
		else if (k !== null && k !== undefined && k !== "")
			el.appendChild(document.createTextNode(k.toString()));
	}
}

const SVGElements: { [key: string]: boolean; }  = {
	svg: true,
	circle: true,
	defs: true,
	ellipse: true,
	g: true,
	line: true,
	linearGradient: true,
	mask: true,
	path: true,
	pattern: true,
	polygon: true,
	polyline: true,
	radialGradient: true,
	rect: true,
	stop: true,
	text: true,
	tspan: true
};

interface ElementConstructor<T> {
	tagName: string;
	extends?: string;
	new(): T;
}

class EventHandlerHook implements virtualDom.VHook {
	private _handler: () => any;

	constructor (fn: any) {
		this._handler = function() {
			fn.apply(this, arguments);
		};
	}

	hook(node: Node, propName: string) { // , previous: any) {
		let evName = propName.substring(2);

		// TODO: how to removeEventListener?
		// if (previous) {
		// 	node.removeEventListener(evName, previous, false);
		// }

		node.addEventListener(evName, this._handler, false);
	}

	unhook(node: Node, propName: string) {
		let evName = propName.substring(2);
		node.removeEventListener(evName, this._handler);
	}
}

// This is needed since `virtual-dom.js` patches objects into constructed
// nodes. Which we don't always want, i.e. Link.to = Flyer(123); we need
// the class preserved.
class ComponentPropertyHook implements virtualDom.VHook {
	constructor(private _val: any) {}

	hook(node: Node, propName: string) {
		(<any> node)[propName] = this._val;
	}

	unhook(node: Node, propName: string) {
		// (<any> node)[propName] = undefined;
	}
}

// Hack checkboxes where virtual-dom is not properly setting the checked value.
// See FW-953
// Note: this alternatively could have been avoided using a custom component
// for input boxes. Eg: flipp-input where the .checked attribute is set to true/false
// in the onRender function
class CheckboxHook implements virtualDom.VHook {
	constructor(private _val: any) {}

	hook(node: Node, propName: string) {
		const checked = this._val ? true : false;
		(node as HTMLInputElement).checked = checked;
	}

	unhook(node: Node, propName: string) {
		(node as HTMLInputElement).checked = false;
	}
}

class ItemNameHook implements virtualDom.VHook {
	constructor(private _val: any) {}

	hook(node: Node, propName: string) {
		(node as HTMLInputElement).value = this._val;
	}

	unhook(node: Node, propName: string) {
		(node as HTMLInputElement).value = "";
	}
}

class ComponentWidget<T> implements virtualDom.Widget {
	type = "Widget";

	constructor(
		private _cls: ElementConstructor<T>,
		public key: string,
		private _attributes: { [x: string]: any },
		public children: virtualDom.VNode[]) {}

	init(): Element {
		const tagName = this._cls.tagName;
		const extension = this._cls.extends;

		const propCopy: { [x: string]: any } = {};
		Object.getOwnPropertyNames(this._attributes).forEach((key) =>
				propCopy[key] = this._attributes[key]);

		const tag = extension ? extension : tagName;

		const el = (<Component<any, any>> (extension ?
				document.createElement(extension, tagName) :
				document.createElement(tagName)));
		el.setVNodeInternal(
				new virtualDom.VNode(tag.toUpperCase(), propCopy, []));
		el.setTranscludedVTreeInternal(this.children);

		// apply the properties
		let diff = virtualDom.diff(
				new virtualDom.VNode(tag.toUpperCase(), {}, []),
				el.vNode);
		virtualDom.patch(el, diff);

		return el as Element;
	}

	update(previous: ComponentWidget<T>, domNode: Element) {
		// check if the transcluded tree has changed
		const component = (<Component<any, any>> domNode);

		// apply the properties
		let diff = virtualDom.diff(
				new virtualDom.VNode(component.tagName, previous._attributes, []),
				new virtualDom.VNode(component.tagName, this._attributes, []));
		virtualDom.patch(component as Element, diff);

		// set the new transcluded children
		component.setTranscludedVTreeInternal(this.children);
	}

	destroy(node: Element) {}
}

// Public Definitions -------------------------------------------------------->

let FLIPP_VIRTUAL_DOM = false;
export function setVirtualDOM(on: boolean) {
	FLIPP_VIRTUAL_DOM = on;
}

export function createFragment(...elements: Appendable[]) {
	const fragment = document.createDocumentFragment();
	appendChildren(fragment, elements);
	return fragment;
}

export function createElementVirtual<T>(
	cls: ElementConstructor<T>|string,
	attributes: { [x: string]: any },
	children: (virtualDom.VNode|string|number)[]): virtualDom.VTree {

	let tagName = (<any> cls).tagName ? (<any> cls).tagName : cls;
	const extension = (<any> cls).extends ? (<any> cls).extends : null;
	const ns = (typeof cls === "string" && cls in SVGElements) ?
			"http://www.w3.org/2000/svg" : null;

	if (extension) {
		attributes["is"] = tagName;
		tagName = extension;
	}

	// clean up the child list that TSX passes us to conform with the child list
	// format that virtual-dom expects.
	let childNodes =
			children
				.map((child) => typeof child === "string" || typeof child === "number" ?
						new virtualDom.VText(`${child}`) : child)
				.filter((child) => child !== null);

	// build the virtual properties list
	let vProperties: {[prop: string]: any} = {
		attributes: {}
	};

	for (let name in attributes) {
		if (!attributes.hasOwnProperty(name)) continue;

		let lName = name.toLowerCase();
		if (lName.substring(0, 2) === "on") {
			let fn = attributes[name];
			vProperties[lName] = new EventHandlerHook(fn);
		} else if (lName === "style" || lName === "key") {
			vProperties[name] = attributes[name];
		} else if (lName === "classlist") {
			const classEntry = vProperties["attributes"]["class"] || "";
			const classList: string[] = [classEntry];
			Object.getOwnPropertyNames(attributes[name]).forEach((prop) => {
				if (attributes[name][prop] && prop !== classEntry) {
					classList.push(prop);
				}
			});
			vProperties["attributes"]["class"] = classList.join(" ");
		} else if (lName === "class") {
			if (vProperties["attributes"][lName]) {
				vProperties["attributes"][lName].unshift(attributes[name]);
			} else {
				vProperties["attributes"][lName] = attributes[name];
			}
		} else if (typeof cls !== "string" &&
								lName !== "class" &&
								Object.getOwnPropertyDescriptor(cls.prototype, name)) {
			// if it is a property on the component class set it directly
			vProperties[name] = new ComponentPropertyHook(attributes[name]);
		} else {
			vProperties["attributes"][name] = attributes[name];
		}
	}

	const key = attributes["key"] || attributes["ref"];
	if (typeof cls === "string") {
		if (tagName === "input" && (attributes["type"] || "").toLowerCase() === "checkbox") {
			// Hack checkboxes where virtual-dom is not properly setting the checked value.
			// See FW-953
			vProperties["flipp-check"] = new CheckboxHook(attributes["checked"]);
		}
		if (tagName === "input" && (attributes["name"] || "").toLowerCase() === "happyitem" && attributes["value"]) {
			// See FW-953
			vProperties["flipp-happy-item-name"] = new ItemNameHook(attributes["value"]);
		}
		return new virtualDom.VNode(tagName, vProperties, childNodes, key, ns);
	} else {
		return new ComponentWidget(cls, key, vProperties, childNodes);
	}
}

export function createElement(element: string, attributes?: { [x: string]: any }, ...children: Appendable[]) {
	const svgElements: { [key: string]: boolean; } = {
		svg: true,
		g: true,
		rect: true,
		circle: true,
		path: true
	};

	attributes = attributes || {};

	const tagName = (<any> element).tagName ? (<any> element).tagName : element;
	const extension = (<any> element).extends ? (<any> element).extends : null;

	if (FLIPP_VIRTUAL_DOM) {
		return createElementVirtual(element, attributes || {},
				children ? (<any[]> array.flatten([].concat(children))) : []);
	}

	const el = svgElements[tagName] ?
			document.createElementNS("http://www.w3.org/2000/svg", tagName) :
			extension ?
					document.createElement(extension, tagName) :
			attributes["is"] ?
					document.createElement(tagName, attributes["is"]) :
			document.createElement(tagName);

	if (extension) el.setAttribute("is", tagName);

	// Check if current component is new style `Component` vs the legacy `CustomElement`.
	const componentStyleCreateElement =
			(<any> el)["COMPONENT_STYLE_CREATE_ELEMENT"];

	for (const k in attributes) {
		if (attributes.hasOwnProperty(k)) {
			const v = attributes[k];

			if (k.substr(0, 2).toLowerCase() === "on") {
				(<any> el)[k.toLowerCase()] = v;
			} else if (typeof v === "boolean" && v) {
				if (v) {
					el.setAttribute(k, "");
				}
			} else if (k.toLowerCase() === "style" && v) {
				Object.getOwnPropertyNames(v).forEach((prop) => (<any>el).style[prop] = v[prop]);
			} else if (k.toLowerCase() === "classlist" && v) {
				Object.getOwnPropertyNames(v).forEach((prop) => {
					if (v[prop] && !el.classList.contains(prop)) {
						el.classList.add(prop);
					}
				});
			} else if (componentStyleCreateElement && (k in el)) {
				(<any> el)[k] = v;
			} else {
				el.setAttribute(k, v);
			}
		}
	}

	if (children) {
		appendChildren(el, children);
	}
	return el;
}
